export type WorkDurationTerminalStatus =
    | 'completed'
    | 'failed'
    | 'interrupted'
    | 'cancelled';

export function parseTimestampMs(
    value: string | null | undefined,
): number | null {
    if (!value) {
        return null;
    }
    const timestampMs = Date.parse(value);
    return Number.isNaN(timestampMs) ? null : timestampMs;
}

function formatCompactDuration(durationMs: number): string {
    const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    if (minutes > 0) {
        return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }
    return `${seconds}s`;
}

export function formatWorkDurationLabel({
    startedAt,
    completedAt,
    nowMs,
    terminalStatus = 'completed',
}: {
    readonly startedAt: string | null | undefined;
    readonly completedAt: string | null | undefined;
    readonly nowMs: number;
    readonly terminalStatus?: WorkDurationTerminalStatus;
}): string | null {
    const startedAtMs = parseTimestampMs(startedAt);
    if (startedAtMs === null) {
        return null;
    }

    const completedAtMs = parseTimestampMs(completedAt);
    if (completedAtMs !== null) {
        const duration = formatCompactDuration(completedAtMs - startedAtMs);
        if (terminalStatus === 'failed') {
            return `Failed after ${duration}`;
        }
        if (terminalStatus === 'interrupted') {
            return `Interrupted after ${duration}`;
        }
        if (terminalStatus === 'cancelled') {
            return `Cancelled after ${duration}`;
        }
        return `Worked for ${duration}`;
    }

    return `Working for ${formatCompactDuration(nowMs - startedAtMs)}`;
}
